import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  updateProfile,
  deleteUser as deleteFirebaseUser,
  onAuthStateChanged,
  User as FirebaseUser,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { User, AuthState } from '../types';

const AuthContext = createContext<AuthState | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || '',
          role: firebaseUser.email === 'admin@admin.com' ? 'admin' : 'user',
          createdAt: Date.now(),
          lastLogin: Date.now()
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Verificar se o email está vazio
      if (!email.trim()) {
        setError('O email é obrigatório');
        return;
      }

      // Verificar se a senha está vazia
      if (!password.trim()) {
        setError('A senha é obrigatória');
        return;
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      if (userCredential.user) {
        setUser({
          uid: userCredential.user.uid,
          email: userCredential.user.email!,
          displayName: userCredential.user.displayName || '',
          role: userCredential.user.email === 'admin@admin.com' ? 'admin' : 'user',
          createdAt: Date.now(),
          lastLogin: Date.now()
        });
      }
    } catch (err: any) {
      console.error('Erro de autenticação:', err);
      
      // Tratamento específico de erros do Firebase
      if (err.code === 'auth/invalid-credential') {
        setError('Email ou senha inválidos');
      } else if (err.code === 'auth/user-not-found') {
        setError('Usuário não encontrado');
      } else if (err.code === 'auth/wrong-password') {
        setError('Senha incorreta');
      } else if (err.code === 'auth/invalid-email') {
        setError('Email inválido');
      } else if (err.code === 'auth/user-disabled') {
        setError('Usuário desativado');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Erro de conexão. Verifique sua internet.');
      } else {
        setError('Erro ao fazer login. Por favor, tente novamente.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setLoading(true);
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      if (userCredential.user) {
        await updateProfile(userCredential.user, { displayName });
        
        setUser({
          uid: userCredential.user.uid,
          email: userCredential.user.email!,
          displayName,
          role: 'user',
          createdAt: Date.now(),
          lastLogin: Date.now()
        });
      }
    } catch (err) {
      setError('Erro ao criar conta. Tente novamente.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      await firebaseSignOut(auth);
      setUser(null);
    } catch (err) {
      setError('Erro ao fazer logout.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    if (!user || !auth.currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      if (userData.displayName) {
        await updateProfile(auth.currentUser, { displayName: userData.displayName });
      }
      
      setUser({
        ...user,
        ...userData,
        displayName: userData.displayName || user.displayName
      });
    } catch (err) {
      setError('Erro ao atualizar usuário.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!user || !auth.currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      await deleteFirebaseUser(auth.currentUser);
      setUser(null);
    } catch (err) {
      setError('Erro ao excluir usuário.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      signIn,
      signUp,
      signOut,
      updateUser,
      deleteUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}; 